import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  ViewChild,
} from "@angular/core";
import { AccountManualType } from "@bitwarden/web-vault/app/models/types/account.types";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountDetailsTableFilter } from "@bitwarden/web-vault/app/components/tables/account-tables/account-details-table/account-details.table";
import { canAnimate, EASE_OUT } from "@bitwarden/web-vault/app/shared/utils/panel-motion.util";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";

import {
  appliedAccountsFilter,
  MANUAL_TYPE_LABEL_KEYS,
  resetAccountsFilter,
  syncedOriginName,
} from "./accounts-filter.utils";

@Component({
  selector: "app-accounts-filter",
  templateUrl: "./accounts-filter.component.html",
})
export class AccountsFilterComponent {
  private cdr = inject(ChangeDetectorRef);
  private elRef = inject(ElementRef);

  @ViewChild("filterToggleButton") private filterToggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild("filterPanel") private filterPanel?: ElementRef<HTMLElement>;

  manualAccountTypes = input<AccountManualType[]>([]);
  syncedOrigins = input<Origin[]>([]);
  manualTypeCounts = input<Partial<Record<AccountManualType, number>>>({});
  syncedOriginCounts = input<Partial<Record<Origin, number>>>({});
  selectedFilter = input.required<AccountDetailsTableFilter>();
  filterChange = output<AccountDetailsTableFilter>();
  /**
   * The Accounts page's filter: a raised round button beside the search box, a popover that drops
   * under it (a bottom sheet on phones) with a count per option, and a dot on the button while
   * something is hidden. Left off, it is the stand-alone form with the raised square button.
   */
  inline = input(false);
  /** Accounts the filter currently lets through, and all of them: "4 of 6 shown" in the header. */
  shownCount = input<number | null>(null);
  totalCount = input<number | null>(null);

  showFilterDropdown: boolean = false;

  protected toggleClasses(): string {
    const open = this.showFilterDropdown;
    if (this.inline()) {
      // Pressed in while the popover is open or a filter is applied, so the button itself says so.
      const pressed = open || this.isFiltered();
      return [
        "tw-relative tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-appearance-none tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-p-0",
        pressed
          ? "tw-bg-color-surface-sunken tw-shadow-elevation-input tw-text-color-text-action"
          : "tw-bg-color-surface-elevated tw-shadow-elevation-control tw-text-color-text-secondary hover:tw-shadow-elevation-card",
      ].join(" ");
    }
    return [
      "border-gradient-neuomorphic tw-shadow-elevation-card tw-box-border tw-relative tw-mb-1 tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-appearance-none tw-items-center tw-justify-center tw-rounded-2xl tw-p-0",
      open ? "tw-bg-dt-surface-elevated" : "tw-bg-color-surface-page",
    ].join(" ");
  }

  readonly displayedSyncedOrigins = computed(() =>
    this.syncedOrigins().filter((o) => o !== Origin.basiq && o !== Origin.plaid),
  );

  // Counts only the filters that are displayed in the UI and actually interactive — a
  // zero-count type/origin is disabled and can't be unchecked by the user, so it must not
  // count as an active choice (otherwise unchecking everything the user can control would
  // still leave the badge showing a stuck, unclearable count).
  private readonly applied = computed(() =>
    appliedAccountsFilter(
      this.selectedFilter(),
      this.manualAccountTypes(),
      this.displayedSyncedOrigins(),
      this.manualTypeCounts(),
      this.syncedOriginCounts(),
    ),
  );

  filterCount = computed(() => this.applied().selected);

  // A filter is active whenever the user has deselected at least one option they can control —
  // including deselecting all of them, which empties the table and most needs an indicator.
  // Keying the badge off this instead of off filterCount() directly avoids the count and the
  // "is anything filtered" state colliding at both 0 and total.
  isFiltered = computed(() => this.applied().applied);

  /** One row per option, for the popover: label, how many accounts, locked or not, ticked or not. */
  protected readonly manualRows = computed(() =>
    this.manualAccountTypes().map((type) => ({
      type,
      labelKey: MANUAL_TYPE_LABEL_KEYS[type] ?? "",
      fallbackLabel: this.capitalize(type),
      count: this.manualTypeCounts()[type],
      disabled: this.isManualTypeDisabled(type),
      checked: !!this.selectedFilter()?.manual?.[type] && !this.isManualTypeDisabled(type),
    })),
  );

  protected readonly syncedRows = computed(() =>
    this.displayedSyncedOrigins().map((origin) => ({
      origin,
      label: syncedOriginName(origin),
      count: this.syncedOriginCounts()[origin],
      disabled: this.isSyncedOriginDisabled(origin),
      checked: !!this.selectedFilter()?.synced?.[origin] && !this.isSyncedOriginDisabled(origin),
    })),
  );

  /** "Show 4 accounts", the sheet's way to close: how many the filter lets through. */
  protected readonly showLabelCount = computed(() => this.shownCount() ?? this.totalCount() ?? 0);

  toggleFilterDropdown() {
    if (this.showFilterDropdown) {
      this.closeFilterDropdown();
    } else {
      this.openFilterDropdown();
    }
  }

  onFilterClick() {
    this.toggleFilterDropdown();
  }

  /**
   * Keyboard/screen-reader users have no other way to reach the panel than the toggle button,
   * so opening it must move focus in — otherwise focus stays stranded on the button with no
   * indication the panel exists (#4511 follow-up).
   */
  private openFilterDropdown(): void {
    this.showFilterDropdown = true;
    // detectChanges() forces the *ngIf-gated panel into the DOM synchronously, so the
    // ViewChild is resolvable and .focus() lands immediately — no setTimeout/render-timing
    // guesswork.
    this.cdr.detectChanges();
    const panel = this.filterPanel?.nativeElement;
    panel?.focus({ preventScroll: true });
    if (panel && this.inline()) {
      this.alignUnderButton(panel);
      this.animateIn(panel);
    }
  }

  /**
   * Hangs the popover off the icon: its right edge lines up with the button's. It stays inside the
   * toolbar row it is positioned against (that sits in an overflow-hidden column that would clip
   * it), so where the button is too near the row's left edge for the full width, it slides right
   * just far enough to fit. The phone sheet is fixed to the screen and is left alone.
   */
  private alignUnderButton(panel: HTMLElement): void {
    const button = this.filterToggleButton?.nativeElement;
    const row = panel.offsetParent;
    if (!button || !row || getComputedStyle(panel).position === "fixed") {
      return;
    }
    const buttonRight = button.getBoundingClientRect().right - row.getBoundingClientRect().left;
    const rightmost = Math.max(row.clientWidth - panel.offsetWidth, 0);
    panel.style.left = `${Math.min(Math.max(buttonRight - panel.offsetWidth, 0), rightmost)}px`;
  }

  /**
   * The popover fades in over 150ms, easing down 4px into place; the phone sheet slides up from the
   * bottom edge instead. Nothing moves for a user who asked for less motion, or where the Web
   * Animations API is missing.
   */
  private animateIn(panel: HTMLElement): void {
    if (!canAnimate() || prefersReducedMotion()) {
      return;
    }
    const sheet = getComputedStyle(panel).position === "fixed";
    panel.animate(
      sheet
        ? [{ transform: "translateY(100%)" }, { transform: "none" }]
        : [
            { opacity: 0, transform: "translateY(-4px)" },
            { opacity: 1, transform: "none" },
          ],
      { duration: sheet ? 280 : 150, easing: EASE_OUT },
    );
  }

  /** A resize (a phone turning over) re-lays the page out under an open popover: close it. */
  @HostListener("window:resize")
  protected onWindowResize(): void {
    this.closeFilterDropdown(false);
  }

  /** Ticks every option again. */
  protected onResetClick(): void {
    this.filterChange.emit(
      resetAccountsFilter(
        this.selectedFilter(),
        this.manualAccountTypes(),
        this.displayedSyncedOrigins(),
      ),
    );
  }

  /**
   * A row is a bigger target than its checkbox: a click anywhere on it toggles it. The checkbox's
   * own click is left to the checkbox, or it would toggle twice.
   */
  protected onManualRowClick(event: Event, type: AccountManualType, checked: boolean): void {
    if (!this.isOnCheckbox(event)) {
      this.onManualTypeFilterChange(type, !checked);
    }
  }

  protected onSyncedRowClick(event: Event, origin: Origin, checked: boolean): void {
    if (!this.isOnCheckbox(event)) {
      this.onSyncedOriginFilterChange(origin, !checked);
    }
  }

  private isOnCheckbox(event: Event): boolean {
    return !!(event.target as HTMLElement | null)?.closest("app-checkbox");
  }

  /**
   * Centralises every dismissal path (outside click, Escape, backdrop, explicit close button,
   * toggle) so `showFilterDropdown` only ever gets set in one place. Guarded on
   * showFilterDropdown because ClickOutsideDirective's Escape handler is global: pressing
   * Escape for an unrelated reason while this panel is already closed must not steal focus.
   * Closing a backgrounded-but-open panel on an unrelated Escape is harmless, so that part
   * isn't further guarded — only the focus restoration is.
   *
   * restoreFocus defaults to true for every explicit close action (X button, backdrop, toggle,
   * Escape) — there's no other sensible focus target, so returning to the button that opened
   * the panel is correct. It must be false for a genuine outside click: the browser already
   * moved focus to whatever the user clicked (mousedown fires, and moves focus, before our
   * mouseup-driven outside-click detection runs) — forcibly refocusing the toggle button would
   * yank focus away from the element the user just clicked into.
   *
   * Even when restoreFocus is true, it only actually moves focus if focus is currently *within
   * this component* — otherwise a stray Escape while this panel is open in the background (the
   * user is really interacting with something else, e.g. an unrelated dialog) would yank focus
   * away from wherever it legitimately is onto this toggle button.
   */
  closeFilterDropdown(restoreFocus = true): void {
    if (!this.showFilterDropdown) {
      return;
    }
    this.showFilterDropdown = false;
    if (restoreFocus && this.elRef.nativeElement.contains(document.activeElement)) {
      this.filterToggleButton?.nativeElement.focus();
    }
  }

  onManualTypeFilterChange(type: AccountManualType, checked: boolean) {
    if (this.isManualTypeDisabled(type)) {
      return;
    }
    const filter = this.selectedFilter();
    const updatedFilter = {
      ...filter,
      manual: { ...filter.manual, [type]: checked },
    };
    this.filterChange.emit(updatedFilter);
  }

  onSyncedOriginFilterChange(origin: Origin, checked: boolean) {
    if (this.isSyncedOriginDisabled(origin)) {
      return;
    }
    const filter = this.selectedFilter();
    const updatedFilter = {
      ...filter,
      synced: { ...filter.synced, [origin]: checked },
    };
    this.filterChange.emit(updatedFilter);
  }

  // A missing key means the parent hasn't resolved counts yet (still loading) — only a count
  // that's actually present and zero means "confirmed no accounts of this type."
  isManualTypeDisabled(type: AccountManualType): boolean {
    return this.manualTypeCounts()[type] === 0;
  }

  isSyncedOriginDisabled(origin: Origin): boolean {
    return this.syncedOriginCounts()[origin] === 0;
  }

  capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  manualTypeLabel(type: AccountManualType): string {
    const count = this.manualTypeCounts()[type];
    return `${this.capitalize(type)} Accounts${count === undefined ? "" : ` (${count})`}`;
  }

  syncedOriginLabel(origin: Origin): string {
    const count = this.syncedOriginCounts()[origin];
    return `${this.capitalize(origin)} Accounts${count === undefined ? "" : ` (${count})`}`;
  }
}
